import React, { useEffect, useState, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import AdminNav from '../../components/admin/AdminNav';
import { GroupContext } from '../../context/GroupContext';

const EPSILON = 0.001;

export default function ViewGroup() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { groups, fetchGroup } = useContext(GroupContext);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [group, setGroup] = useState(null);
  const [showMembers, setShowMembers] = useState(false);

  useEffect(() => {
    let cancelled = false;

    async function ensureGroupLoaded() {
      setLoading(true);
      setError('');
      try {
        let foundGroup = groups.find((g) => g._id === id);

        if (!foundGroup && fetchGroup) {
          foundGroup = await fetchGroup(id);
        }

        if (!foundGroup && !cancelled) {
          setError('Group not found');
          setGroup(null);
        } else if (!cancelled) {
          setGroup(foundGroup);
        }
      } catch (err) {
        console.error(err);
        if (!cancelled) setError('Failed to load group details');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    ensureGroupLoaded();

    return () => {
      cancelled = true;
    };
  }, [id, groups, fetchGroup]);

  useEffect(() => {
    const updatedGroup = groups.find((g) => g._id === id);
    if (updatedGroup) {
      setGroup(updatedGroup);
    }
  }, [groups, id]);

  if (loading) return <p className="text-center mt-6">Loading group details...</p>;
  if (error) return <p className="text-red-600 text-center mt-6">{error}</p>;
  if (!group) return <p className="text-center mt-6">Group not found</p>;

  const remainingCost = Number(group.remainingCost ?? group.totalCost);
  const effectiveStatus =
    group.status === 'closed'
      ? 'closed'
      : remainingCost <= EPSILON
      ? 'full'
      : 'open';

  const totalCost = parseFloat(group.totalCost) || 0;
  const totalPortions = parseInt(group.totalPortions ?? 0, 10);
  const portionsTaken = parseInt(group.portionsTaken ?? 0, 10);
  const costPerPortion = totalCost && totalPortions ? totalCost / totalPortions : 0;

  const isClosedOrFull = effectiveStatus === 'full' || effectiveStatus === 'closed';
  const remainingPortions = isClosedOrFull ? 0 : totalPortions - portionsTaken;
  const computedRemainingCost = isClosedOrFull ? 0 : remainingCost;

  return (
    <div className="min-h-screen bg-gray-100">
      <AdminNav />
      <div className="max-w-md mx-auto mt-10 p-6 bg-white shadow-md rounded">
        <h1 className="text-3xl font-bold mb-6 text-center text-gray-800">
          Group Details
        </h1>

        <div className="space-y-3 text-gray-700">
          <Detail label="Name" value={group.name} />
          <Detail label="Supplier" value={group.supplierId?.name || 'N/A'} />
          <Detail label="State" value={group.state} />
          <Detail label="Animal" value={group.animal} />

          {/* Collapsible Members */}
          <div className="border-b pb-2">
            <div
              className="flex justify-between items-center cursor-pointer select-none"
              onClick={() => setShowMembers((prev) => !prev)}
            >
              <span className="font-medium">
                Members {group.members?.length ? `(${group.members.length})` : ''}
              </span>
              <span className="text-gray-500">{showMembers ? '▲' : '▼'}</span>
            </div>

            {showMembers && (
              <ul
                className="mt-2 list-disc pl-5 border rounded p-2"
                style={{
                  maxHeight: group.members?.length > 3 ? 150 : 'auto',
                  overflowY: group.members?.length > 3 ? 'auto' : 'visible',
                  minHeight: 30,
                }}
              >
                {group.members?.length ? (
                  group.members.map((m, idx) => (
                    <li key={idx}>{m.email}</li>
                  ))
                ) : (
                  <li>No members yet</li>
                )}
              </ul>
            )}
          </div>

          <Detail
            label="Total Cost"
            value={
              totalCost
                ? `$${totalCost.toLocaleString(undefined, {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}`
                : 'N/A'
            }
          />
          <Detail label="Total Portions" value={group.totalPortions ?? 'N/A'} />
          <Detail
            label="Portions Available"
            value={group.portionsAvailable?.length ? group.portionsAvailable.join(', ') : 'N/A'}
          />
          <Detail label="Remaining Cost to Share" value={`$${computedRemainingCost.toFixed(2)}`} />
          <Detail label="Portions Remaining" value={isNaN(remainingPortions) ? 'N/A' : remainingPortions} />
          <Detail label="Cost Per Portion" value={`$${costPerPortion.toFixed(2)}`} />
          <Detail label="Effective Status" value={effectiveStatus} />
          <Detail label="Stored Status" value={group.status} />
          <Detail
            label="Created At"
            value={group.createdAt ? new Date(group.createdAt).toLocaleString() : '—'}
          />
          <Detail
            label="Last Updated"
            value={group.updatedAt ? new Date(group.updatedAt).toLocaleString() : '—'}
          />
          <Detail
            label="Date Closed"
            value={group.dateClosed ? new Date(group.dateClosed).toLocaleString() : 'Not Closed'}
          />
        </div>

        <div className="flex justify-center mt-6 space-x-4">
          <button
            onClick={() => navigate(-1)}
            className="bg-gray-500 text-white px-4 py-2 rounded hover:bg-gray-600"
          >
            Back
          </button>
          <button
            onClick={() => navigate(`/admin/groups/edit/${group._id}`)}
            className="bg-yellow-500 text-white px-4 py-2 rounded hover:bg-yellow-600"
          >
            Edit Group
          </button>
        </div>
      </div>
    </div>
  );
}

function Detail({ label, value }) {
  const getStatusColor = (status) => {
    switch (status) {
      case 'open':
        return 'bg-green-100 text-green-800 px-2 py-1 rounded';
      case 'full':
        return 'bg-yellow-100 text-yellow-800 px-2 py-1 rounded';
      case 'closed':
        return 'bg-gray-200 text-gray-800 px-2 py-1 rounded';
      default:
        return '';
    }
  };
  const isStatus = label.toLowerCase().includes('status');
  return (
    <div className="flex justify-between border-b pb-2 items-center">
      <span className="font-medium">{label}:</span>
      <span className={isStatus ? getStatusColor(value) : ''}>{value}</span>
    </div>
  );
}
